import './compose_page.less';

import * as Business          from '../business';
import * as Campaigns         from '../campaigns';
import * as Groups            from '../groups';
import * as UI                from '../ui';
import { Navbar as F7Navbar } from 'framework7-react';
import { useCallback }        from 'react';
import { useEffect }          from 'react';
import { usePageContext }     from '../components/render_page';
import { useRef }             from 'react';
import autosize               from 'autosize';
import ConfirmModal           from '../components/confirm_modal';
import PhoneIcon              from './phone_icon.svg';
import pluralize              from '../util/pluralize';
import React                  from 'react';
import RecipientsChooser      from './recipients_chooser';
import useBoolean             from '../hooks/use_boolean';
import useObjectState         from '../hooks/use_object_state';
import useRouter              from '../hooks/use_router';


export default function ComposePage({ businessID, initialRecord, pageTitle }) {
  const [ savedRecord, updateSavedRecord ] = useObjectState(initialRecord);
  const [ record, updateRecord ]           = useObjectState(savedRecord);

  const saveCampaign     = Campaigns.useSaveCampaign(businessID);
  const scheduleCampaign = Campaigns.useScheduleCampaign(businessID);

  async function save() {
    const result = await saveCampaign(record);
    updateSavedRecord(result);
    updateRecord(result);
    return result;
  }

  async function send() {
    const result = await scheduleCampaign(record);
    updateSavedRecord(result);
    updateRecord(result);
    return result;
  }

  return (
    <>
      <ComposeNavbar {...{ record, savedRecord, pageTitle, save, send }}/>
      <div className="page-content">
        <RecipientsField {...{ record, updateRecord }}/>
        <SubjectField {...{ record, updateRecord }}/>
        <Message {...{ record, updateRecord }}/>
      </div>
    </>
  );
}


function ComposeNavbar({ record, savedRecord, pageTitle, save, send }) {
  const { businessID }     = usePageContext();
  const { navigateTo }     = usePageContext();
  const { navigateBackTo } = usePageContext();
  const router             = useRouter();
  const toast              = UI.useToast();

  const isChanged = isRecordChanged(savedRecord, record);
  const isValid   = isRecordValid(record);

  const backLabel    = isChanged ? 'Save' : 'Back';
  const sendDisabled = !isValid;

  const [ confirmIsOpen, showConfirm, hideConfirm ] = useBoolean();

  async function onClickBack() {
    if (isChanged) {
      await save();
      toast('Campaign saved!');
      const activityHref = `/${businessID}/campaigns/activity`;
      navigateBackTo(activityHref, { force: true });
    } else
      router.back();
  }

  async function onClickConfirm() {
    hideConfirm();
    const result = await send();
    toast('Campaign scheduled!');
    const href = `/${businessID}/campaigns/${result.id}`;
    navigateTo(href, { reloadCurrent: true });
  }

  const numberOfPeople = record.recipients.length;
  const personOrPeople = (numberOfPeople === 1) ? 'person' : 'people';
  const oneMinute      = 1000 * 60;
  const minutes        = Campaigns.scheduledCampaignDelay / oneMinute;

  return (
    <>
      <F7Navbar title={pageTitle}>
        <div className="left">
          <span className="link back-link" onClick={onClickBack}><i className="icon icon-back"></i> {backLabel}</span>
        </div>
        <div className="right">
          <span className="link send-link" disabled={sendDisabled} onClick={showConfirm}>Send</span>
        </div>
      </F7Navbar>
      <ConfirmModal
        isOpen={confirmIsOpen}
        closeModal={hideConfirm}
        onConfirm={onClickConfirm}
        title="Send Campaign"
        description={(
          <>
            Your email will be sent to {numberOfPeople}&nbsp;{personOrPeople}.
            You’ll have {minutes}&nbsp;{pluralize(minutes, 'minute')} to change your&nbsp;mind.
          </>
        )}
        buttonLabel="Send Campaign"
        buttonClassName="danger"
      />
    </>
  );
}


function RecipientsField({ record, updateRecord }) {
  const [ chooserIsOpen, showChooser, hideChooser ] = useBoolean();

  const hasRecipients = (record.recipients.length > 0);

  return (
    <div className="field recipients">
      <label>Recipients</label>
      {hasRecipients && <FilledRecipientsField {...{ record, showChooser }}/>}
      {!hasRecipients && <EmptyRecipientsField {...{ showChooser }}/>}
      <RecipientsChooser {...{ chooserIsOpen, hideChooser, record, updateRecord }}/>
    </div>
  );
}


function EmptyRecipientsField({ showChooser }) {
  return (
    <div className="field-content">
      <div className="pick-contacts">
        <span className="link" onClick={showChooser}>Pick Contacts</span>
      </div>
    </div>
  );
}

function FilledRecipientsField({ record, showChooser }) {
  const currentGroup = Groups.useGroup(record.group);
  const count        = record.recipients.length;
  const title        = currentGroup?.title ?? 'Custom Selection';
  return (
    <div className="field-content">
      <div className="group">
        <span className="group-token">
          {title}
          <span className="badge">{count}</span>
        </span>
      </div>
      <div className="pick-contacts">
        <span className="link" onClick={showChooser}>Change</span>
      </div>
    </div>
  );
}


function SubjectField({ record, updateRecord }) {
  const field               = 'subject';
  const [ value, onChange ] = useRecordField({ record, updateRecord, field });
  const placeholder         = getPlaceholder(field);

  return (
    <div className="field subject">
      <label>Subject</label>
      <AutosizeTextarea
        name={field}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </div>
  );
}


function Message({ record, updateRecord }) {
  const field               = 'body';
  const [ value, onChange ] = useRecordField({ record, updateRecord, field });
  const placeholder         = getPlaceholder(field);

  const { businessID } = usePageContext();
  const business       = Business.useBusiness(businessID);
  const bannerImage    = Business.useBannerImage(businessID);

  return (
    <div className="message-preview">
      <img src={bannerImage} className="banner-image"/>
      <p>
        Hello <span className="token" data-tooltip="The recipient’s name will automatically be included in the message greeting.">Customer Name</span>
      </p>
      <div className="editor">
        <AutosizeTextarea
          name={field}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
      </div>
      <CtaSection/>
      <BusinessAddress {...{ business }}/>
      <MessageFooter/>
    </div>

  );
}


function CtaSection() {
  return (
    <div className="cta-section">
      <p className="cta">
        <span className="cta-button">
          <PhoneIcon/>
          Call us today
        </span>
      </p>

      <p>
        You can <span className="link">call us</span>, reply to this email, or <span className="link">visit our website</span> for more information.
      </p>
    </div>
  );
}


function BusinessAddress({ business }) {
  if (!business)
    return null;

  const { name }                = business;
  const { contact }             = business;
  const { street, city, state } = contact;
  return (
    <p className="address">
      {name}<br/>
      {street}<br/>
      {city}, {state}
    </p>
  );
}


function MessageFooter() {
  return (
    <p className="footer">
      To stop receiving emails from us, you can <span className="link">unsubscribe</span>.
    </p>
  );
}


function AutosizeTextarea({ name, value, onChange, placeholder }) {
  const ref = useRef();

  useEffect(function initializeAutosize() {
    const textarea = ref.current;
    if (textarea) {
      autosize(textarea);
      return () => {
        autosize.destroy(textarea);
      };
    } else
      return () => { };
  }, [ ref ]);

  useEffect(function resizeWhenValueChanges() {
    const textarea = ref.current;
    if (textarea)
      autosize.update(textarea);
  }, [ ref, value ]);

  return (
    <textarea
      ref={ref}
      name={name}
      value={value}
      rows={1}
      onChange={onChange}
      onKeyUp={onChange}
      placeholder={placeholder}
    />
  );
}


function useRecordField({ record, updateRecord, field }) {
  const value    = record[field];
  const onChange = useCallback(function onChange({ target }) {
    updateRecord({
      [field]: target.value
    });
  }, [ field, updateRecord ]);
  return [ value, onChange ];
}


function isRecordChanged(savedRecord, record) {
  return Object.keys(record).some(key => savedRecord[key] !== record[key]);
}

export function isRecordValid(record) {
  return !!(record.subject && record.recipients?.length && record.body);
}


function getPlaceholder(field) {
  switch (field) {
    case 'subject':
      return 'Your email subject goes here';
    case 'body':
      return 'Your email content goes here';
    default:
      return null;
  }
}
